import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import type { SearchType } from 'lib/dot/enum/searchType.ts';
import type { PageType } from 'lib/dot/enum/pageType.ts';
import { SavedSearchFrequency } from 'lib/dot/enum/savedSearchFrequency.ts';
import type {
	RequestCreateWatchdogFilter,
	WatchdogDataFilter,
	WatchdogNotificationInterval,
} from 'service/watchdog/model.ts';
import type { SearchFiltersAnalytics } from 'lib/dot/utils.ts';
import { getLocalitySeoName } from 'utils/getLocalityName.ts';
import { categoriesMap } from 'signals/category';
import type { LocalityData } from 'service/locality/model.ts';
import { getKdeParameter } from 'utils/locality';

// When the saved search is edited or saved for the first time
export type SavedSearchAnalytics = {
	action: 'saveSearch';
	actionType: 'save' | 'edit';
	categoryCount: number;
	categoryId: number;
	filterValues: SearchFiltersAnalytics;
	frequency: SavedSearchFrequency;
	itemCount: number;
	pageType: PageType;
	queryValue?: string;
	searchType: SearchType;
};

// When inside the modal a user clicks on 'change' button on either filter or location
export type ChangeSavedSearchAnalytics = {
	action: 'saveSearch';
	actionType: 'changeDialog';
	changeType: 'locality' | 'price';
};

export type DeleteSavedSearchAnalytics = {
	action: 'saveSearch';
	actionType: 'delete';
	categoryCount: number;
	filterValues: SearchFiltersAnalytics;
	frequency: SavedSearchFrequency;
	itemCount: number;
	pageType: PageType;
	queryValue?: string;
	searchType: SearchType;
};

export type OpenSavedSearchModalAnalytics = {
	action: 'saveSearch';
	actionType: 'openDialog';
	categoryCount: number;
	filterValues: SearchFiltersAnalytics;
	itemCount: number;
	pageType: PageType;
	searchType: SearchType;
};

export function sendSavedSearchAnalytics(
	watchdogFilter: Omit<RequestCreateWatchdogFilter, 'locality'> & { locality?: LocalityData },
	watchdogFrequency: WatchdogNotificationInterval,
	actionType: 'save' | 'edit',
) {
	const store = DOTHelper.getStore();

	const data: SavedSearchAnalytics = {
		action: 'saveSearch',
		actionType,
		categoryCount: store.categoryCount,
		categoryId: watchdogFilter.category_id ?? 0,
		filterValues: watchdogRemoteFilterToAnalyticsFilter(watchdogFilter),
		frequency: frequencyToAnalyticsFrequency(watchdogFrequency),
		itemCount: store.itemCount,
		pageType: store.pageType,
		searchType: store.searchType,
	};

	return DOTHelper.hit(data);
}

export function sendDeleteSavedSearchSearchAnalytics(
	watchdogFilter: WatchdogDataFilter,
	watchdogFrequency: WatchdogNotificationInterval,
) {
	const store = DOTHelper.getStore();

	const data: DeleteSavedSearchAnalytics = {
		action: 'saveSearch',
		actionType: 'delete',
		categoryCount: store.categoryCount,
		filterValues: watchdogFilterToAnalyticsFilter(watchdogFilter),
		frequency: frequencyToAnalyticsFrequency(watchdogFrequency),
		itemCount: store.itemCount,
		pageType: store.pageType,
		searchType: store.searchType,
	};

	return DOTHelper.hit(data);
}

export function sendOpenSavedSearchModalAnalytics(watchdogFilter: WatchdogDataFilter) {
	const store = DOTHelper.getStore();

	const data: OpenSavedSearchModalAnalytics = {
		action: 'saveSearch',
		actionType: 'openDialog',
		categoryCount: store.categoryCount,
		filterValues: watchdogFilterToAnalyticsFilter(watchdogFilter),
		itemCount: store.itemCount,
		pageType: store.pageType,
		searchType: store.searchType,
	};

	return DOTHelper.hit(data);
}

export function sendOpenNewSavedSearchModalAnalytics(
	watchdogFilter: Omit<RequestCreateWatchdogFilter, 'locality'> & { locality?: LocalityData },
) {
	const store = DOTHelper.getStore();

	const data: OpenSavedSearchModalAnalytics = {
		action: 'saveSearch',
		actionType: 'openDialog',
		categoryCount: store.categoryCount,
		filterValues: watchdogRemoteFilterToAnalyticsFilter(watchdogFilter),
		itemCount: store.itemCount,
		pageType: store.pageType,
		searchType: store.searchType,
	};

	return DOTHelper.hit(data);
}

export function sendChangeSavedSearchAnalytics(changeType: 'locality' | 'price') {
	const data: ChangeSavedSearchAnalytics = {
		action: 'saveSearch',
		actionType: 'changeDialog',
		changeType,
	};

	return DOTHelper.hit(data);
}

export function frequencyToAnalyticsFrequency(sort: WatchdogNotificationInterval): SavedSearchFrequency {
	switch (sort) {
		case 'daily':
			return SavedSearchFrequency.DAILY;
		case 'hourly':
			return SavedSearchFrequency.HOURLY;
		case 'never':
			return SavedSearchFrequency.NONE;
	}
}

export function watchdogFilterToAnalyticsFilter(watchdogFilter: WatchdogDataFilter): SearchFiltersAnalytics {
	return {
		'with-deal': DOTHelper.sanitizeBoolean(!watchdogFilter.hidePriceByAgreement),
		categoryId: watchdogFilter.categoryId?.toString(),
		categoryName: watchdogFilter.categoryId ? categoriesMap.peek()[watchdogFilter.categoryId]?.seoName : undefined,
		kde: watchdogFilter.locality && getKdeParameter(watchdogFilter.locality),
		locality: watchdogFilter.locality && getLocalitySeoName(watchdogFilter.locality),
		phrase: watchdogFilter.phrase,
		price: watchdogFilter.priceForFree
			? 'for-free'
			: watchdogFilter.priceFrom || watchdogFilter.priceTo
				? 'custom'
				: undefined,
		priceFrom: watchdogFilter.priceFrom,
		priceTo: watchdogFilter.priceTo,
		radius: watchdogFilter.radiusPrecision,
		usedGoods: 0,
	};
}

export function watchdogRemoteFilterToAnalyticsFilter(
	watchdogFilter: Omit<RequestCreateWatchdogFilter, 'locality'> & { locality?: LocalityData },
): SearchFiltersAnalytics {
	return {
		'with-deal': DOTHelper.sanitizeBoolean(!watchdogFilter.hide_price_by_agreement),
		categoryId: watchdogFilter.category_id?.toString(),
		categoryName: watchdogFilter.category_id
			? categoriesMap.peek()[watchdogFilter.category_id]?.seoName
			: undefined,
		kde: watchdogFilter.locality && getKdeParameter(watchdogFilter.locality),
		locality: watchdogFilter.locality && getLocalitySeoName(watchdogFilter.locality),
		phrase: watchdogFilter.phrase,
		price: watchdogFilter.price_for_free
			? 'for-free'
			: watchdogFilter.price_from || watchdogFilter.price_to
				? 'custom'
				: undefined,
		priceFrom: watchdogFilter.price_from,
		priceTo: watchdogFilter.price_to,
		radius: watchdogFilter.radius_precision,
		usedGoods: 0,
	};
}
